/**
 * TypeScript mirrors of `moment_core.schemas.api` (Pydantic v2) and the enums in `moment_core.models.enums`.
 * Keep in sync with the backend; dates are ISO 8601 strings, UUIDs are strings.
 */

// ---------- enums ----------
export type SourceType =
  | 'youtube_video'
  | 'youtube_channel'
  | 'youtube_playlist'
  | 'youtube_search'
  | 'url_list'
  | 'publisher_feed'
  | 'ctv_catalog';
export type SourceStatus = 'pending' | 'discovering' | 'ready' | 'error';
export type Platform = 'youtube' | 'web' | 'publisher' | 'ctv';
export type AnalysisStatus = 'queued' | 'analyzing' | 'done' | 'failed' | 'skipped';
export type AnalysisTier = 'standard' | 'premium';
export type Sentiment = 'positive' | 'neutral' | 'negative';
export type Risk = 'low' | 'medium' | 'high';
export type Granularity = 'video' | 'moment';
export type MatchMode = 'any' | 'all';
export type SegmentStatus = 'draft' | 'active' | 'archived';
export type ExportType = 'dv360_placement_list' | 'dv360_custom_bidding' | 'csv' | 'json' | 'webhook';
export type ExportStatus = 'queued' | 'running' | 'done' | 'failed';
export type PublisherStatus = 'active' | 'paused' | 'disabled';
export type CreativeFormat = 'banner_bottom' | 'lower_third' | 'product_card' | 'side_panel' | 'simid';
export type CampaignStatus = 'draft' | 'active' | 'paused' | 'ended';
export type JobStatus = 'queued' | 'running' | 'done' | 'failed';
export type ApiScope = 'read' | 'write' | 'sdk' | 'export';

export const SOURCE_TYPES: SourceType[] = [
  'youtube_video',
  'youtube_channel',
  'youtube_playlist',
  'youtube_search',
  'url_list',
  'publisher_feed',
  'ctv_catalog',
];
export const ANALYSIS_STATUSES: AnalysisStatus[] = ['queued', 'analyzing', 'done', 'failed', 'skipped'];
export const PLATFORMS: Platform[] = ['youtube', 'web', 'publisher', 'ctv'];
export const SENTIMENTS: Sentiment[] = ['positive', 'neutral', 'negative'];
export const RISKS: Risk[] = ['low', 'medium', 'high'];
export const SEGMENT_STATUSES: SegmentStatus[] = ['draft', 'active', 'archived'];
export const PUBLISHER_STATUSES: PublisherStatus[] = ['active', 'paused', 'disabled'];
export const CREATIVE_FORMATS: Exclude<CreativeFormat, 'simid'>[] = [
  'banner_bottom',
  'lower_third',
  'product_card',
  'side_panel',
];
export const CAMPAIGN_STATUSES: CampaignStatus[] = ['draft', 'active', 'paused', 'ended'];

// ---------- generic ----------
export interface Page<T> {
  items: T[];
  next_cursor: string | null;
  total?: number | null;
}

// ---------- api keys ----------
export interface ApiKeyCreate {
  name: string;
  scopes: ApiScope[];
}
export interface ApiKeyOut {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
}
export interface ApiKeyCreated extends ApiKeyOut {
  key: string;
}

// ---------- sources ----------
export interface SourceConfig {
  fps?: number | null;
  resolution?: 'high';
  language?: string | null;
  max_videos?: number | null;
  date_from?: string | null;
  tier?: AnalysisTier;
  urls?: string[] | null;
  /** url_list made of uploaded files: storage key per URL, same order as `urls`. */
  upload_keys?: string[] | null;
  feed_adapter?: string | null;
  csv_text?: string | null;
  publisher_id?: string | null;
  region?: string | null;
  relevance_language?: string | null;
  order?: 'relevance' | 'viewCount' | 'date' | 'rating' | null;
  video_duration?: 'any' | 'short' | 'medium' | 'long' | null;
  safe_search?: 'none' | 'moderate' | 'strict' | null;
}
export interface SourceCreate {
  type: SourceType;
  external_id: string;
  title?: string | null;
  config?: SourceConfig;
}
export interface SourceOut {
  id: string;
  type: SourceType;
  external_id: string;
  title: string | null;
  thumbnail_url: string | null;
  status: SourceStatus;
  config: Record<string, unknown>;
  error: string | null;
  created_at: string;
  updated_at: string;
  video_count: number;
  analyzed_count: number;
  /** Videos queued or analyzing right now. Older API builds omit it; treat undefined as 0. */
  active_count?: number;
  total_duration_sec: number;
  estimated_cost_usd: number;
}
export interface EstimateOut {
  video_count: number;
  total_duration_sec: number;
  model: string;
  estimated_cost_usd: number;
}

// ---------- uploads ----------
export interface UploadUrlOut {
  key: string;
  /** Presigned (7 days) or public URL of the stored file. */
  url: string;
}
export interface UploadOut extends UploadUrlOut {
  filename: string;
  size_bytes: number;
}

// ---------- videos / moments ----------
export interface BrandOut {
  name: string;
  confidence: number;
  visibility: string;
}
export interface ProductOut {
  name: string;
  category: string;
  confidence: number;
  bbox_hint?: string | null;
}
export interface PersonOut {
  name: string | null;
  role: string;
  is_celebrity: boolean;
  confidence: number;
}
export interface BrandSafetyOut {
  garm_category?: string | null;
  risk: string;
  flags: string[];
}
export interface MomentOut {
  id: string;
  video_id: string;
  start_sec: number;
  end_sec: number;
  scene_summary: string;
  transcript: string;
  sentiment: Sentiment;
  emotions: string[];
  topics: string[];
  iab_categories: string[];
  objects: string[];
  products: ProductOut[];
  brands: BrandOut[];
  people: PersonOut[];
  setting: string | null;
  activity: string | null;
  brand_safety: BrandSafetyOut;
  keyframe_url?: string | null;
}
export interface VideoOut {
  id: string;
  source_id: string | null;
  platform: Platform;
  external_id: string;
  url: string;
  title: string | null;
  description: string | null;
  channel_name: string | null;
  published_at: string | null;
  duration_sec: number | null;
  language: string | null;
  thumbnail_url: string | null;
  analysis_status: AnalysisStatus;
  analysis_tier: AnalysisTier;
  fps_used: number | null;
  model_used: string | null;
  token_input: number | null;
  token_output: number | null;
  cost_usd: number | null;
  analyzed_at: string | null;
  overall_summary: string | null;
  overall_iab_categories: string[];
  overall_brand_safety: Record<string, unknown> | null;
  error: string | null;
  skip_reason: string | null;
  created_at: string;
  moment_count: number;
}
export interface VideoDetail extends VideoOut {
  moments: MomentOut[];
}
export interface AnalyzeRequest {
  tier?: AnalysisTier | null;
  fps?: number | null;
  model?: string | null;
}
export interface BulkAnalyzeRequest extends AnalyzeRequest {
  video_ids?: string[] | null;
  source_id?: string | null;
  only_unanalyzed?: boolean;
}
export interface BulkAnalyzeOut {
  queued: number;
}
export interface VideoListParams {
  source_id?: string;
  status?: AnalysisStatus;
  platform?: Platform;
  q?: string;
  limit?: number;
}

// ---------- explore ----------
export interface MomentSearchHit {
  id: string;
  video_id: string;
  video_title: string;
  channel: string;
  platform: string;
  video_external_id: string;
  start_sec: number;
  end_sec: number;
  scene_summary: string;
  transcript: string;
  topics: string[];
  brands: string[];
  products: string[];
  setting: string;
  activity: string;
  sentiment: string;
  iab: string[];
  risk: string;
  keyframe_url?: string | null;
  thumbnail_url?: string | null;
  score?: number | null;
  highlights?: Record<string, unknown> | null;
}
export type FacetName = 'topics' | 'brands' | 'setting' | 'sentiment' | 'iab' | 'risk' | 'channel';
export const FACET_NAMES: FacetName[] = [
  'topics',
  'brands',
  'setting',
  'sentiment',
  'iab',
  'risk',
  'channel',
];
export interface MomentSearchOut {
  hits: MomentSearchHit[];
  total: number;
  facets: Record<string, Record<string, number>>;
  semantic: boolean;
}
export interface MomentSearchParams {
  q?: string;
  semantic?: boolean;
  threshold?: number;
  limit?: number;
  offset?: number;
  video_id?: string[];
  topics?: string[];
  brands?: string[];
  setting?: string[];
  sentiment?: string[];
  iab?: string[];
  risk?: string[];
  channel?: string[];
}

// ---------- segments ----------
export type RuleField =
  | 'topics'
  | 'emotions'
  | 'iab_categories'
  | 'objects'
  | 'sentiment'
  | 'setting'
  | 'activity'
  | 'brands'
  | 'products'
  | 'people'
  | 'brand_safety.risk'
  | 'brand_safety.flags'
  | 'channel'
  | 'platform'
  | 'language'
  | 'transcript'
  | 'scene_summary'
  | 'semantic'
  | 'duration_sec';
export type RuleOp =
  | 'contains_any'
  | 'contains_all'
  | 'not_contains'
  | 'in'
  | 'not_in'
  | 'eq'
  | 'neq'
  | 'text_match'
  | 'similar_to'
  | 'gte'
  | 'lte';
export interface Rule {
  field: RuleField;
  op: RuleOp;
  value: string | string[] | number;
  threshold?: number | null;
  min_confidence?: number | null;
}
export interface Rules {
  include: Rule[];
  exclude: Rule[];
}
export interface SegmentCreate {
  name: string;
  description?: string | null;
  rules: Rules;
  granularity?: Granularity;
  match_mode?: MatchMode;
  min_confidence?: number;
  status?: SegmentStatus;
  webhook_url?: string | null;
}
export interface SegmentUpdate {
  name?: string;
  description?: string | null;
  rules?: Rules;
  granularity?: Granularity;
  match_mode?: MatchMode;
  min_confidence?: number;
  status?: SegmentStatus;
  webhook_url?: string | null;
  regenerate_webhook_secret?: boolean;
}
export interface SegmentOut {
  id: string;
  name: string;
  description: string | null;
  rules: Rules;
  granularity: Granularity;
  match_mode: MatchMode;
  min_confidence: number;
  status: SegmentStatus;
  last_computed_at: string | null;
  video_count: number;
  moment_count: number;
  webhook_url: string | null;
  has_webhook_secret: boolean;
  created_at: string;
  updated_at: string;
}
export interface SegmentPreviewRequest {
  rules: Rules;
  granularity: Granularity;
  match_mode: MatchMode;
  min_confidence: number;
}
export interface SegmentMemberOut {
  video_id: string;
  moment_id: string | null;
  score: number;
  video_title?: string | null;
  video_external_id?: string | null;
  platform?: string | null;
  url?: string | null;
  thumbnail_url?: string | null;
  channel_name?: string | null;
  start_sec?: number | null;
  end_sec?: number | null;
  scene_summary?: string | null;
  topics: string[];
  brands: string[];
}
export interface SegmentPreviewOut {
  total: number;
  video_total: number;
  moment_total: number;
  sample: SegmentMemberOut[];
}
export interface WebhookSecretOut {
  webhook_url: string | null;
  webhook_secret: string | null;
}

// ---------- exports ----------
export interface ExportCreate {
  segment_id: string;
  type: ExportType;
  config?: Record<string, unknown>;
}
export interface ExportOut {
  id: string;
  segment_id: string;
  type: ExportType;
  config: Record<string, unknown>;
  status: ExportStatus;
  result: Record<string, unknown> | null;
  error: string | null;
  created_at: string;
  updated_at: string;
}

// ---------- publishers / overlay ----------
export interface PublisherCreate {
  name: string;
  domain: string;
  extra_domains?: string[];
  revenue_share_pct?: number;
}
export interface PublisherUpdate {
  name?: string;
  domain?: string;
  extra_domains?: string[];
  revenue_share_pct?: number;
  status?: PublisherStatus;
}
export interface PublisherOut {
  id: string;
  name: string;
  domain: string;
  extra_domains: string[];
  sdk_key: string;
  status: PublisherStatus;
  revenue_share_pct: number;
  created_at: string;
  video_count: number;
  embed_snippet: string;
}
export interface PublisherVideoCreate {
  player_video_id: string;
  video_id?: string | null;
  video_url?: string | null;
  page_url?: string | null;
}
export interface PublisherVideoOut {
  id: string;
  publisher_id: string;
  video_id: string;
  player_video_id: string;
  page_url: string | null;
  created_at: string;
}
/** OpenSincera summary of a publisher domain (GET /publishers/lookup, POST /publishers/prospects). */
export interface PublisherLookupOut {
  domain: string;
  name: string | null;
  primary_supply_type: string | null;
  categories: string[];
  players: string[];
  own_player: boolean;
  a2cr: number | null;
  ads_in_view: number | null;
  page_weight: number | null;
  supply_paths: number | null;
  resellers: number | null;
  owner_domain: string | null;
  score: number;
  reasons: string[];
  sincera_url: string | null;
  depth: number;
}
export interface ProspectsRequest {
  seeds: string[];
  categories?: string[];
  depth?: number;
  max_publishers?: number;
  require_own_player?: boolean;
}
export interface ProspectsOut {
  items: PublisherLookupOut[];
  api_calls: number;
  cached: boolean;
}

export interface CreativeAssets {
  headline?: string;
  body?: string;
  cta_text?: string;
  cta_url?: string;
  image_url?: string;
  logo_url?: string;
  brand_color?: string;
  [key: string]: unknown;
}
export interface CreativeCreate {
  name: string;
  format: CreativeFormat;
  html_template?: string | null;
  assets?: CreativeAssets;
  width?: number | null;
  height?: number | null;
  duration_sec?: number;
  dismissible?: boolean;
}
export interface CreativeUpdate {
  name?: string;
  html_template?: string;
  assets?: CreativeAssets;
  width?: number | null;
  height?: number | null;
  duration_sec?: number;
  dismissible?: boolean;
}
export interface CreativeOut {
  id: string;
  name: string;
  format: CreativeFormat;
  html_template: string;
  assets: CreativeAssets;
  width: number | null;
  height: number | null;
  duration_sec: number;
  dismissible: boolean;
  rendered_url: string | null;
  created_at: string;
}
export interface CreativePreviewOut {
  html: string;
}
export type CreativeTemplates = Record<string, string>;

export interface FrequencyCap {
  per_user_per_day: number | null;
  per_video: number | null;
}
export interface TriggerConfig {
  min_moment_score: number;
  delay_sec: number;
  only_first_match: boolean;
}
export interface CampaignCreate {
  name: string;
  advertiser_name?: string | null;
  segment_id: string;
  creative_id: string;
  publisher_ids?: string[];
  start_at?: string | null;
  end_at?: string | null;
  frequency_cap?: FrequencyCap;
  trigger?: TriggerConfig;
  budget_usd?: number | null;
  cpm_usd?: number;
  status?: CampaignStatus;
}
export interface CampaignPatch {
  name?: string;
  advertiser_name?: string | null;
  publisher_ids?: string[];
  start_at?: string | null;
  end_at?: string | null;
  frequency_cap?: FrequencyCap;
  trigger?: TriggerConfig;
  budget_usd?: number | null;
  cpm_usd?: number;
  status?: CampaignStatus;
}
export interface CampaignOut {
  id: string;
  name: string;
  advertiser_name: string | null;
  segment_id: string;
  creative_id: string;
  publisher_ids: string[];
  start_at: string | null;
  end_at: string | null;
  frequency_cap: Partial<FrequencyCap>;
  trigger: Partial<TriggerConfig>;
  budget_usd: number | null;
  cpm_usd: number;
  status: CampaignStatus;
  created_at: string;
  updated_at: string;
}
export interface StatsRow {
  key: string;
  impressions: number;
  viewable: number;
  clicks: number;
  dismisses: number;
  ctr: number;
  viewability: number;
  spend_usd: number;
}
export interface CampaignStats {
  totals: StatsRow;
  by_day: StatsRow[];
  by_publisher: StatsRow[];
  by_video: StatsRow[];
}

// ---------- costs / jobs ----------
export interface CostRow {
  period: string;
  model: string;
  videos: number;
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
  /** Set once the API reports failed attempts in the ledger; absent on older builds. */
  status?: string | null;
}
export interface CostsOut {
  granularity: 'day' | 'month';
  rows: CostRow[];
  total_cost_usd: number;
  total_videos: number;
  avg_cost_per_video_usd: number;
  projected_month_usd: number;
}
export interface JobOut {
  id: string;
  task_id: string | null;
  type: string;
  ref_type: string | null;
  ref_id: string | null;
  status: JobStatus;
  progress: number;
  message: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
}
/** Payload pushed on `/ws/jobs` (see `moment_core.jobs.job_payload`). */
export interface JobPush {
  id: string;
  type: string;
  ref_type: string | null;
  ref_id: string | null;
  status: JobStatus;
  progress: number;
  message: string | null;
  error: string | null;
  updated_at: string;
}
export interface JobListParams {
  status?: JobStatus;
  type?: string;
  ref_id?: string;
  limit?: number;
}

// ---------- campaign performance (apps/api/moment_api/routers/performance.py) ----------
export type PerformanceReportKind = 'auto' | 'dv360' | 'google_ads';
export const PERFORMANCE_REPORT_KINDS: { value: PerformanceReportKind; label: string }[] = [
  { value: 'auto', label: 'Detect from columns' },
  { value: 'dv360', label: 'DV360 report' },
  { value: 'google_ads', label: 'Google Ads placement report' },
];
export interface PerformanceTotals {
  impressions: number;
  views: number;
  clicks: number;
  conversions: number;
  spend_usd: number;
  view_rate: number | null;
  ctr: number | null;
  cpv: number | null;
  cpm: number | null;
  conversion_rate: number | null;
  rows: number;
}
export interface PerformanceImportRequest {
  file: File;
  kind: PerformanceReportKind;
  campaign_ref?: string;
  segment_id?: string;
  export_id?: string;
  /** ISO dates; the end date is stored on rows of reports without a date column. */
  date_from?: string;
  date_to?: string;
}
export interface PerformanceImportOut {
  kind: string;
  campaign_ref: string;
  rows: number;
  matched: number;
  unmatched_count: number;
  /** First 20 placement ids that did not match a video in the library. */
  unmatched: string[];
  totals: PerformanceTotals;
  date_from: string | null;
  date_to: string | null;
}
export interface PerformanceVideoRow extends PerformanceTotals {
  video_id: string | null;
  external_id: string;
  platform: string;
  title: string | null;
  url: string | null;
  thumbnail_url: string | null;
  channel_name: string | null;
  /** Segment score (0..1) when the summary is scoped to a segment. */
  score: number | null;
  date_from: string | null;
  date_to: string | null;
}
export interface PerformanceSummaryOut {
  totals: PerformanceTotals;
  videos: PerformanceVideoRow[];
  campaigns: string[];
  date_from: string | null;
  date_to: string | null;
}
export interface PerformanceSummaryParams {
  segment_id?: string;
  video_id?: string;
  campaign_ref?: string;
}
export interface PerformanceCampaignOut {
  source: string;
  campaign_ref: string;
  segment_id: string | null;
  totals: PerformanceTotals;
  videos: number;
  unmatched: number;
  date_from: string | null;
  date_to: string | null;
}
export interface PerformanceCampaignsOut {
  items: PerformanceCampaignOut[];
}
